/**
 * `/brouillon/tutoriel` : le didacticiel de la feuille de brouillon en page.
 *
 * L'exercice guidé de la démo, à la classe de l'élève (`?niveau=`, sinon celle de son profil). Ouvert par la carte
 * « Découvre la feuille de brouillon » des tableaux de bord et, à la fin de l'inscription, avant le tableau de bord
 * ou le diagnostic (`?apres=tableau|diagnostic`). Rien n'est enregistré.
 */

import { useNavigate, useRouter } from '@tanstack/react-router';
import { PageLoader } from '@/components/spinner';
import { useAuth } from '@/lib/auth';
import { useStudentProfile } from '@/hooks/use-student-profile';
import { GuidedTutorial } from './guided-tutorial';
import type { TutorialNext } from './first-use';

const DONE_LABEL: Record<TutorialNext, string> = {
  tableau: 'Aller à mon tableau de bord',
  diagnostic: 'Commencer mon diagnostic',
};

export function ScratchTutorialPage({ niveau, apres }: { niveau?: string; apres?: TutorialNext }) {
  const router = useRouter();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  // sans `?niveau=` : la classe de l'élève connecté (attendue, pour ne pas changer d'exercice en route)
  const student = useStudentProfile(!niveau && profile?.role === 'student' ? user?.id : undefined);
  if (!niveau && student.isLoading) return <PageLoader label="Préparation de ta feuille…" />;

  function done() {
    if (apres === 'diagnostic') {
      void navigate({ to: '/student/diagnostic', search: { start: 1 } });
      return;
    }
    if (apres === 'tableau') {
      if (profile?.role === 'student') void navigate({ to: '/student/dashboard' });
      else if (profile?.role === 'player') void navigate({ to: '/player/dashboard' });
      else void navigate({ to: '/' });
      return;
    }
    if (window.history.length > 1) router.history.back();
    else void navigate({ to: '/' });
  }

  return (
    <GuidedTutorial
      level={niveau ?? student.data?.level ?? null}
      context={apres ? 'inscription' : 'page'}
      intro={
        apres
          ? 'Ton compte est prêt. Avant de commencer, découvre ta feuille de brouillon.'
          : undefined
      }
      doneLabel={apres ? DONE_LABEL[apres] : 'C’est parti !'}
      onDone={done}
    />
  );
}
